import React, { useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { avatarColor } from '../lib/avatars';
import './ChannelSettingsModal.css';

const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

export default function ChannelSettingsModal({ chat, onClose, onIconUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(chat.icon_url || null);
  const fileInputRef = useRef(null);

  if (!chat) return null;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setUploadError('Only JPEG, PNG, or WebP images are supported.');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setUploadError('Image must be under 2MB.');
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${chat.id}/${Date.now()}.${ext}`;

      const { data: uploadData, error: uploadErr } = await supabase.storage
        .from('channel-icons')
        .upload(path, file, { upsert: true });

      if (uploadErr) throw uploadErr;

      const { data: { publicUrl } } = supabase.storage
        .from('channel-icons')
        .getPublicUrl(uploadData.path);

      const { error: updateErr } = await supabase
        .from('chats')
        .update({ icon_url: publicUrl })
        .eq('id', chat.id);

      if (updateErr) throw updateErr;

      setPreviewUrl(publicUrl);
      onIconUpdated(chat.id, publicUrl);
    } catch (err) {
      setUploadError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveIcon = async () => {
    setUploadError('');
    setUploading(true);
    try {
      const { error } = await supabase
        .from('chats')
        .update({ icon_url: null })
        .eq('id', chat.id);
      if (error) throw error;
      setPreviewUrl(null);
      onIconUpdated(chat.id, null);
    } catch (err) {
      setUploadError(err?.message || 'Failed to remove icon.');
    } finally {
      setUploading(false);
    }
  };

  const letter = chat.name.charAt(0).toUpperCase();
  const fallbackBg = avatarColor(chat.id);

  return (
    <div className="room-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div
        className="room-modal ch-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Channel Settings"
        onClick={e => e.stopPropagation()}
      >
        <div className="ch-settings-header">
          <h3 className="room-modal-title">Channel Settings</h3>
          <button className="ch-close-btn" onClick={onClose} aria-label="Close settings">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <p className="room-modal-subtitle ch-section-label">Channel Icon</p>

        <div className="ch-icon-preview-row">
          <div
            className="ch-icon-preview"
            style={{ background: previewUrl ? undefined : fallbackBg }}
          >
            {previewUrl
              ? <img src={previewUrl} alt={chat.name} />
              : <span className="ch-icon-letter">{letter}</span>
            }
          </div>

          <div className="ch-icon-actions">
            <button
              className="dash-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : 'Upload image'}
            </button>
            {previewUrl && (
              <button
                className="dash-btn dash-btn-secondary"
                onClick={handleRemoveIcon}
                disabled={uploading}
              >
                Remove
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        <p className="ch-settings-hint">JPEG, PNG, or WebP · Max 2MB</p>

        {uploadError && (
          <p className="members-invite-status err">{uploadError}</p>
        )}
      </div>
    </div>
  );
}
